import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { COL } from '@eprintsmart/shared';
import { db } from '../firebase';

/**
 * Reading the AI-assistant referral counters.
 *
 * Read STRAIGHT FROM FIRESTORE rather than through a callable. The rules already
 * make both collections admin-read and client-write-denied, so the client cannot
 * see or forge anything it should not; and the functions codebase carries
 * secret-bound exports that make every deploy of it a gated, manual affair. A
 * report that needs no new server code is a report that ships today.
 *
 * Two collections, deliberately: the per-day counter answers "how many" without
 * ever scanning the raw rows, and the raw rows answer "landing on what" for a
 * bounded window. Reading the counter for totals means the headline number stays
 * cheap however much traffic accumulates.
 */

/** Raw rows scanned for the landing-path breakdown. Bounded on purpose. */
const MAX_ROWS = 1000;

interface DailyDoc {
  day: string;
  total?: number;
  bySource?: Record<string, number>;
  updatedAt?: number;
}

interface VisitRow {
  source: string;
  path: string;
  at: number;
}

export interface LlmReferralSummary {
  /** Referrals inside the window, from the per-day counters. */
  total: number;
  bySource: { source: string; count: number; sharePercent: number }[];
  byDay: { day: string; total: number }[];
  topPaths: { path: string; count: number }[];
  /**
   * When anything was last recorded, IGNORING the window. This is what tells a
   * genuinely quiet fortnight apart from a beacon that has stopped writing —
   * the failure this endpoint has already had once, where it returned 204 and
   * recorded nothing while looking perfectly healthy.
   */
  lastRecordedAt: number | null;
  /** True when the path breakdown hit MAX_ROWS and is therefore a sample. */
  pathsTruncated: boolean;
}

/**
 * Day keys are UTC, because the writer builds them with toISOString(). In IST
 * that puts the boundary at 05:30 rather than midnight, so a late-evening visit
 * still lands on the day it felt like. Only the first 5.5 hours of an Indian
 * morning fall into the previous key.
 */
function utcDayKey(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export async function getLlmReferralSummary(days: number): Promise<LlmReferralSummary> {
  const cutoffMs = Date.now() - days * 24 * 60 * 60 * 1000;
  const startDay = utcDayKey(cutoffMs);

  const [dailySnap, rowsSnap, latestSnap] = await Promise.all([
    getDocs(query(collection(db, COL.llmVisitDaily), where('day', '>=', startDay), orderBy('day'))),
    getDocs(
      query(
        collection(db, COL.llmVisits),
        where('at', '>=', cutoffMs),
        orderBy('at', 'desc'),
        limit(MAX_ROWS),
      ),
    ),
    // Deliberately unfiltered by window — see lastRecordedAt above.
    getDocs(query(collection(db, COL.llmVisitDaily), orderBy('day', 'desc'), limit(1))),
  ]);

  const daily = dailySnap.docs.map((d) => d.data() as DailyDoc);

  let total = 0;
  const sourceCounts = new Map<string, number>();
  for (const d of daily) {
    total += d.total ?? 0;
    for (const [source, count] of Object.entries(d.bySource ?? {})) {
      sourceCounts.set(source, (sourceCounts.get(source) ?? 0) + count);
    }
  }

  const rows = rowsSnap.docs.map((d) => d.data() as VisitRow);
  const pathCounts = new Map<string, number>();
  for (const r of rows) pathCounts.set(r.path, (pathCounts.get(r.path) ?? 0) + 1);

  const latest = latestSnap.docs[0]?.data() as DailyDoc | undefined;

  return {
    total,
    bySource: [...sourceCounts.entries()]
      .map(([source, count]) => ({
        source,
        count,
        // Against the counter total, not the sampled rows, so the shares add up
        // to the headline number the card is showing.
        sharePercent: total > 0 ? Math.round((count / total) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count),
    byDay: daily.map((d) => ({ day: d.day, total: d.total ?? 0 })),
    topPaths: [...pathCounts.entries()]
      .map(([path, count]) => ({ path, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10),
    lastRecordedAt: latest?.updatedAt ?? null,
    pathsTruncated: rowsSnap.size === MAX_ROWS,
  };
}
